import React, { useContext } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { useLocation } from '@app/utils';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-alpine.css';
import ReactPaginate from 'react-paginate';
import {
  BackgroundContainer,
  Container,
  MunicipalityIcon,
  MuncipalityName,
  FiltersContainer,
  FilterBox,
  SearchBox,
  ButtonBox,
  ButtonsCard,
  GridContainer,
  StatusGroup,
  StatusBox,
  GridBox,
  Loader,
  Pagination,
} from './muncipalities-list-components';
import { Button, Icon, SearchNew } from '@app/components';
import StatusBar from '@app/components/status-bar';
import { useGetMuncipalitiesQuery } from '@app/graphql';
import MobileMunicipalityList from './municipality-mobile-list';
import { createMunicipality } from '@app/utils/permissions';
import { AuthContext } from '@app/providers';

const defaultColDef = {
  filter: false,
};
const statusFilterTypes = ['All', 'Active', 'Inactive'];

const CellStyle = {
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
};
const noRowData =
  '<span style="font-size:18px;">Municipalites not found</span>';

export default function MuncipalityListView() {
  const { navigate } = useLocation();
  const { userInfo }: any = useContext(AuthContext);
  const [searchText, setSearchText] = React.useState<string>('');
  const [filterBy, updateFilterBy] = React.useState({
    status: '',
    address: '',
  });
  const [active, setActive] = React.useState(statusFilterTypes[0]);
  const [totalPages, setTotalPages] = React.useState<number>(1);
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [getMuniciaplitiesList] = useGetMuncipalitiesQuery();
  const [params, setParams] = React.useState({
    search: '',
    page: currentPage,
    length: 20,
    sortBy: '',
    filterBy: filterBy,
    municipalityId:
      userInfo && userInfo.municipalityId ? userInfo.municipalityId : '',
  });
  const [rowData, updateRowData] = React.useState([]);
  const [isMobile, setIsMobile] = React.useState(
    window.innerWidth < 768 ? true : false,
  );
  const [loading, setLoading] = React.useState(true);

  window.addEventListener('resize', function() {
    if (window.innerWidth < 768) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  });

  React.useEffect(() => {
    getMunicipaities(params);
    // eslint-disable-next-line
  }, []);

  /**
   *
   * @param params parameters required to get municipalities list
   */
  function getMunicipaities(params: any) {
    setLoading(true);
    getMuniciaplitiesList({ variables: params })
      .then((response: any) => {
        if (response && response.data && response.data.municipalities) {
          const {
            currentPage,
            municipality,
            success,
            totalPages,
          } = response.data.municipalities;
          setCurrentPage(currentPage);
          if (success) {
            setTotalPages(totalPages);
            if (municipality) {
              updateRowData(municipality);
              setParams({ ...params, page: currentPage + 1 });
            }
          }
          setLoading(false);
        }
      })
      .catch((error: any) => {
        setLoading(false);
      });
  }

  const muncipalityData = [
    {
      headerName: 'MUNICIPALITY',
      field: 'muncipality',
      flex: 1,
      cellStyle: CellStyle,
      tooltipValueGetter: (params: any) => params.value,
      cellRendererFramework: (data: any) => (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'center',
          }}>
          <MunicipalityIcon
            image={data && data.data && data.data.logo ? data.data.logo : ''}
          />
          <MuncipalityName>{data.data.name}</MuncipalityName>
        </div>
      ),
    },
    {
      headerName: 'LOCATION',
      field: 'address',
      flex: 1,
      tooltipValueGetter: (params: any) => params.value,
    },

    {
      headerName: 'EMAIL',
      field: 'email',
      flex: 1,
      tooltipValueGetter: (params: any) => params.value,
    },
    {
      headerName: 'PHONE',
      field: 'phoneNumber',
      width: 200,
      tooltipValueGetter: (params: any) => params.value,
    },
    {
      headerName: 'STATUS',
      field: 'status',
      width: 180,
      cellStyle: CellStyle,
      tooltipValueGetter: (params: any) => params.value,
      cellRendererFramework: (data: any) => (
        <StatusBar
          variant={data.data.status === 'ACTIVE' ? 'primary' : 'secondary'}>
          {data.data.status}
        </StatusBar>
      ),
    },
  ];

  const onRowClicked = (data: any) => {
    const muncipalityId = data.data.id;
    navigate('/municipalities/details/' + muncipalityId);
  };

  const onFilterChange = (type: string) => {
    setActive(type);
    if (type === 'All') {
      updateFilterBy({
        status: '',
        address: '',
      });
      getMunicipaities({
        ...params,
        page: 1,
        filterBy: { status: '', address: '' },
      });
    } else {
      updateFilterBy({
        status: type.toLocaleUpperCase(),
        address: '',
      });
      getMunicipaities({
        ...params,
        page: 1,
        filterBy: { status: type.toLocaleUpperCase(), address: '' },
      });
    }
  };

  const handlePageClick = (data: any) => {
    let selected = data.selected;
    setCurrentPage(selected + 1);
  };

  /**
   *
   * @param e event
   * @param type refers field changed
   */
  const onChange = (e: any, type?: string) => {
    const { value = '' } = e && e.target;
    if (type === 'search') {
      setSearchText(value);
      if (value && value.length >= 3) {
        getMunicipaities({ ...params, page: 1, search: value });
      } else {
        getMunicipaities({ ...params, page: 1, search: '' });
      }
    }
  };

  const onClear = () => {
    setSearchText('');
    getMunicipaities({
      ...params,
      page: 1,
      search: '',
    });
  };
  return (
    <>
      <BackgroundContainer>
        <Container>
          {!isMobile && (
            <>
              <FiltersContainer>
                <FilterBox>
                  <StatusGroup>
                    {statusFilterTypes.map(type => (
                      <StatusBox key={type} active={active === type}>
                        <Button
                          variant="text"
                          size="medium"
                          width="100%"
                          onClick={() => {
                            onFilterChange(type);
                          }}>
                          {type}
                        </Button>
                      </StatusBox>
                    ))}
                  </StatusGroup>

                  <SearchBox>
                    <SearchNew
                      placeholder="Search"
                      value={searchText}
                      onChange={(e: any) => onChange(e, 'search')}
                      variant="medium"
                      onClear={onClear}
                    />
                  </SearchBox>
                </FilterBox>
                {createMunicipality(userInfo) ? (
                  <ButtonsCard>
                    <ButtonBox>
                      <Button
                        variant="primary"
                        size="medium"
                        width="100%"
                        onClick={() => navigate('/municipalities/create')}>
                        Create Municipality
                      </Button>
                    </ButtonBox>
                  </ButtonsCard>
                ) : null}
              </FiltersContainer>
              <GridContainer>
                {loading && (
                  <Loader>
                    <Icon name="loader" />
                  </Loader>
                )}
                <GridBox className="ag-theme-alpine ag-custom-styles">
                  <AgGridReact
                    columnDefs={muncipalityData}
                    defaultColDef={defaultColDef}
                    onRowClicked={onRowClicked}
                    overlayNoRowsTemplate={noRowData}
                    rowData={rowData}
                    headerHeight="32"
                    rowHeight="48"
                    suppressCellSelection
                    suppressHorizontalScroll
                  />
                </GridBox>
              </GridContainer>
            </>
          )}
          {isMobile && (
            <MobileMunicipalityList
              statusFilterTypes={statusFilterTypes}
              municipalityList={rowData}
              active={active}
              setActive={setActive}
              onFilterChange={onFilterChange}
            />
          )}
          {totalPages > 1 && (
            <Pagination>
              <ReactPaginate
                previousLabel={'Previous'}
                nextLabel={'Next'}
                breakLabel={'.....'}
                pageCount={totalPages}
                marginPagesDisplayed={1}
                pageRangeDisplayed={2}
                onPageChange={handlePageClick}
                containerClassName={'pagination'}
                pageLinkClassName={'pageLinkClassName'}
                previousLinkClassName={'previousLinkClassName'}
                nextLinkClassName={'nextLinkClassName'}
                activeLinkClassName={'activeLinkClassName'}
                disabledClassName={'disabledClassName'}
              />
            </Pagination>
          )}
        </Container>
      </BackgroundContainer>
    </>
  );
}
